import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from 'react-intl'
import { Maybe } from '../../common/types'
import { Button, buttonStyle } from '../Button'
import { getReverseGeocodeResult } from '../googlemaps'
import { messages } from './messages'

interface Props {
  className?: string
  onReverseGeocode: (result: Maybe<ReverseGeocodeResults & CurrentLocationCoords>) => void
  onError?: (error: string) => void
  category?: keyof typeof buttonStyle
  useStyle?: boolean
  size?: 'xl' | 'l' | 's' | 'm' | 'ml'
  maxWidth?: string
  disabled?: boolean
}

interface ReverseGeocodeResults {
  address: string
  city: string
  zipcode: string
}

interface CurrentLocationCoords {
  coords: {
    lat: number
    long: number
  }
}

export interface BrowserLocationResponse {
  data: any
}

export const ReverseGeocodeButton: React.FunctionComponent<Props> = ({
  className,
  onReverseGeocode,
  onError,
  category = 'locate',
  useStyle = true,
  size,
  maxWidth,
  disabled,
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const intl = useIntl()
  const onClick = useCallback(
    () => async () => {
      setLoading(true)

      window.navigator.geolocation.getCurrentPosition(
        async (position: { coords: { latitude: number; longitude: number } }) => {
          const result = await getReverseGeocodeResult({lat: position.coords.latitude, lng: position.coords.longitude})
        
          if(!result) {
            onError && onError('Reverse geolocation returned an error')
            return
          }
  
          setLoading(false)
          onReverseGeocode({
            coords: {lat: position.coords.latitude, long: position.coords.longitude},
            address: result.address,
            city: result.city,
            zipcode: result.zipcode
          })
        },
        () => {
          setLoading(false)
          if (onError) {
            onError('Reverse geolocation failed to get location from browser')
          }
        }
      )
    },
    [onError, onReverseGeocode, setLoading]
  )

  return (
    <StyledButton
      category={category}
      className={className}
      onClick={onClick()}
      loading={loading}
      label={intl.formatMessage(messages.locate)}
      alignLeft={useStyle}
      useStyle={useStyle}
      size={size}
      maxWidth={maxWidth}
      disabled={disabled}
    />
  )
}

export const StyledButton = styled(Button as any)`
  ${props =>
    props.useStyle &&
    css`
      margin-top: 1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      width: auto;
    `}
`
